import { getLocale } from '../i18n/locale'
import { ConsentCategories, getCookie, handleCookieYesConsent } from '../scenarios/storage'
import { mapAdditionalInfoLead, mapZipAdditionalInfoLead } from '../scenarios/utils'

import type { AnswerId, Answers, Scenario, StepId } from '../state/interfaces'
import { ScenarioStep } from '../state/interfaces'

// The source of truth for this analytics module is there:
// @link https://docs.google.com/spreadsheets/d/1rG5L2tjro7Zo-SopYWDK_KfOLsIrascMZK5RBVooSpI/edit#gid=0

// Set it to `true` to display useful messages in the console.
// These debug statements are stripped from the code in production by the JavaScript minifier.
const DEBUG = false

// const TRACKING_EVENT_NAME_STEP_PREFIX = 'questionnaire_'

const trackingIsActivated = process.env.REACT_APP_TRACKER_ACTIVE === 'true' ? true : false

const EmptyResolvedPromise = Promise.resolve()

type StepProperties = { id: string; answers: AnswerId[] }
type EventProperties = Record<string, any>

const answersHistory: StepProperties[] = []
let currentScenario: Scenario | null = null
let currentScenarioIsFinished = false
let previousStepId: string | null = null
let currentStepId: string | null = null

export const trackScenarioStarted = (scenario: Scenario): Promise<void> => {
  currentScenario = scenario

  const eventProperties: EventProperties = {
    object: 'Start Mobile Step Questionnaire',
    position: 1, //screens' "positions" are indexed from 1 in the analytics
    scenario: scenario.id,
  }
  return trackEvent('start', eventProperties)
}

export const createNewScenarioInitialInformation = (
  scenarioInitialInformation: Record<string, any> | undefined,
): Record<string, any> => {
  const newScenarioInitialInformation: Record<string, any> = {}
  const mapAdditionalInfo: Record<string, any> = mapAdditionalInfoLead()
  const mapZipAdditionalInfo: Record<string, any> = mapZipAdditionalInfoLead()

  if (scenarioInitialInformation) {
    for (const key in scenarioInitialInformation) {
      if (key in mapAdditionalInfo) {
        newScenarioInitialInformation[mapZipAdditionalInfo[mapAdditionalInfo[key]]] = scenarioInitialInformation[key]
      }
    }
  }

  return newScenarioInitialInformation
}

export const trackAddedStep = (stepId: StepId, answerIds: AnswerId[], answers: Answers): Promise<void> => {
  // Our Scenarios use kebab-case but our tracking events use snake_case, let's manage that:
  const stepIdForTracking = scenarioStepIdToTrackingStepId(stepId)

  const eventProperties: EventProperties = {
    answers: answerIds,
    scenario: currentScenario?.id,
  }

  if (stepId === 'email-address-lead') {
    if (getCookie('scenario')) {
      const scenarioInitialInformation = JSON.parse(getCookie('scenario')).initialInformation
      const newScenarioInitialInformation = createNewScenarioInitialInformation(scenarioInitialInformation)
      const codedScenarioInitialInformation = btoa(JSON.stringify(newScenarioInitialInformation))
      eventProperties['provider'] = scenarioInitialInformation.provider || 'not set'
      eventProperties['planName'] = scenarioInitialInformation.planName || 'not set'
      eventProperties['minutes'] = scenarioInitialInformation.sr_voice || 'not set'
      let data = scenarioInitialInformation.sr_data || 'not set'
      if (data !== 'not set') {
        data = data.replace(/\s/g, '')
      }
      eventProperties['dataInitial'] = data
      eventProperties['planDataUsage'] = scenarioInitialInformation.planDataUsage || 'not set'
      eventProperties['downloadSpeed'] = scenarioInitialInformation.downloadSpeed || 'not set'
      eventProperties['planMonthlyPrice'] = scenarioInitialInformation.planMonthlyPrice || 'not set'
      eventProperties['planUpfrontPrice'] = scenarioInitialInformation.planUpfrontPrice || 'not set'
      eventProperties['cei'] = codedScenarioInitialInformation
    }
  }

  if (currentScenario) {
    eventProperties['position'] = Object.keys(answers).length + 1
    const nextStepId = getNextStepId(currentScenario, stepId, answerIds)
    if (nextStepId) {
      eventProperties['next_question'] = scenarioStepIdToTrackingStepId(nextStepId)
    }
  }
  if (previousStepId) {
    eventProperties['previous_question'] = previousStepId
  }

  updateIdentityTraitAfterStep(stepId, answerIds)
  const eventTrackingPromise = trackEvent(stepIdForTracking, eventProperties)

  previousStepId = stepIdForTracking
  const currentStep: StepProperties = { id: stepIdForTracking, answers: answerIds }
  answersHistory.push(currentStep)

  if (currentScenario && 'next_question' in eventProperties) {
    setTimeout(function setCurrentStepIdAfterStepEffectivelyAdded() {
      currentStepId = eventProperties['next_question']
    }, 1)
  }

  return eventTrackingPromise
}

export const trackUserClosingTheApp = (answers: Answers): Promise<void> => {
  if (currentScenarioIsFinished) {
    // The window is closing _because_ the questionnaire is finished, which already has its own tracking event.
    // Let's not send the "close" event in such a case.
    return EmptyResolvedPromise
  }

  const eventProperties = getEndEventProperties(answers)

  return trackEvent('close', eventProperties)
}

export const trackUserIsRedirectedToResult = (answers: Answers): Promise<void> => {
  currentScenarioIsFinished = true

  const eventProperties = getEndEventProperties(answers)

  return trackEvent('result', eventProperties)
}

export const trackBackAction = (): Promise<void> => {
  const eventProperties: EventProperties = {
    scenario: currentScenario?.id,
    from: currentStepId,
    to: previousStepId,
  }
  return trackEvent('back', eventProperties)
}

function trackEvent(event: string, properties: EventProperties): Promise<void> {
  // if (!event.startsWith(TRACKING_EVENT_NAME_STEP_PREFIX)) {
  //   event = TRACKING_EVENT_NAME_STEP_PREFIX + event
  // }
  DEBUG && console.log('*********** trackEvent()', event, properties)

  return new Promise<void>((resolve) => {
    if (handleCookieYesConsent(ConsentCategories.Analytics) == 'yes') {
      window._cio.track('CIO: ' + event, properties, function onTrackingComplete() {
        resolve()
      })
    } else {
      return EmptyResolvedPromise
    }
  })
}

function customerIoIdentify(answerIds: AnswerId[]) {
  window._cio.identify({ id: answerIds[0], locale: getLocale() })
}

function updateIdentityTraitAfterStep(stepId: StepId, answerIds: AnswerId[]): void {
  switch (stepId) {
    case 'email-address':
    case 'email-address-optional':
    case 'email-address-optional-with-alert':
    case 'email-address-newsletter':
    case 'email-address-lead':
      if (answerIds.length === 1) {
        if (answerIds[0]) {
          customerIoIdentify(answerIds)
        }
      }
      break
  }
}

function scenarioStepIdToTrackingStepId(stepId: StepId): string {
  if (trackingIsActivated) {
    return stepId.replaceAll('_', '-')
  }

  return ''
}

function getStepById(scenario: Scenario, stepId: StepId): ScenarioStep | undefined {
  return scenario.steps.find((step) => step.id === stepId)
}

function getNextStepId(scenario: Scenario, stepId: StepId, answerIds: AnswerId[]): StepId | undefined {
  if (!answerIds.length) {
    return
  }
  const step = getStepById(scenario, stepId)
  if (!step) {
    return
  }
  if (step.next) {
    return step.next // this step always have the same "next" step
  }
  // Some of our steps have dynamic "next" steps, depending on the value of the question.
  // Let's resolve this:
  const stepAnswer = answerIds[0]
  step.answers.find((answer) => answer.id === stepAnswer)
}

function getEndEventProperties(answers: Answers): EventProperties {
  const eventProperties: EventProperties = {
    scenario: currentScenario?.id,
  }
  if (currentScenario) {
    if (answersHistory.length >= 1) {
      const currentStepTrackingId = answersHistory[answersHistory.length - 1].id
      eventProperties['current_question'] = currentStepTrackingId
      eventProperties['position'] = Object.keys(answers).length
    }
    if (answersHistory.length >= 2) {
      const previousStepTrackingId = answersHistory[answersHistory.length - 2].id
      eventProperties['previous_question'] = previousStepTrackingId
    }
  }

  return eventProperties
}
